/**
 * ScoringConfigV2 - V2-only Scoring Configuration Admin UI
 * 
 * Displays and manages scoring configuration for:
 * - Common Funder Enforcement (Phase 1)
 * - Kickback / Wash Trading (Phase 2)
 * - Spend Ratio
 * - Share Cap (Phase 3 - LAST)
 * - Verification Weights
 * 
 * Config Sources:
 * - db.scoring_config (_id: "singleton") for enforcement mechanisms
 * - db.settings (key: "scoring_settings") for verification weights
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Info, 
  Shield, 
  RefreshCw, 
  Save, 
  AlertCircle,
  CheckCircle,
  Loader2,
  HelpCircle,
  Settings,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Tooltip component with info icon
const Tooltip = ({ text }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle className="w-4 h-4 text-[#888] cursor-help inline" />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg -top-2 left-6 transition-opacity">
      {text}
      <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 -left-1 top-3"></div>
    </div>
  </div>
);

// Status badge component
const StatusBadge = ({ label, value, variant = 'default' }) => {
  const colors = {
    default: 'bg-gray-100 text-gray-700',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-amber-100 text-amber-700',
    danger: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700',
  };
  
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#f5f3f0]">
      <span className="text-xs text-[#666]">{label}:</span>
      <span className={`text-xs font-medium px-2 py-0.5 rounded ${colors[variant]}`}>{value}</span>
    </div>
  );
};

// Input field with label and tooltip
const ConfigInput = ({ 
  label, 
  tooltip, 
  value, 
  onChange, 
  type = 'number',
  step = '0.01',
  min,
  max,
  disabled = false,
  error = null,
  placeholder = '',
  prefix = '',
  suffix = ''
}) => (
  <div className="space-y-1">
    <label className="text-sm text-[#666] flex items-center">
      {label}
      {tooltip && <Tooltip text={tooltip} />}
    </label>
    <div className="flex items-center gap-2">
      {prefix && <span className="text-sm text-[#888]">{prefix}</span>}
      <Input
        type={type}
        step={step}
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`bg-[#d4cfc4] border-[#b8b3ab] ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${error ? 'border-red-500' : ''}`}
      />
      {suffix && <span className="text-sm text-[#888]">{suffix}</span>}
    </div>
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

// Toggle switch with label and tooltip
const ConfigToggle = ({ label, tooltip, checked, onChange, disabled = false }) => (
  <div className="flex items-center justify-between py-2">
    <label className="text-sm text-[#666] flex items-center">
      {label}
      {tooltip && <Tooltip text={tooltip} />}
    </label>
    <Switch
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      className="data-[state=checked]:bg-[#f0b90b]"
    />
  </div>
);

// Mechanism card wrapper
const MechanismCard = ({ 
  title, 
  badge = null,
  warning = null,
  notes = [],
  children, 
  onSave, 
  saving = false,
  dirty = false,
  lastUpdated = null,
  updatedBy = null
}) => (
  <Card className="p-5 bg-white border-[#b8b3ab]">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <h3 className="text-md font-bold text-[#1a1a1a]">{title}</h3>
        {badge && (
          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">
            {badge}
          </span>
        )}
      </div>
      {lastUpdated && (
        <span className="text-xs text-[#888]">
          Updated: {new Date(lastUpdated).toLocaleDateString()}
          {updatedBy && ` by ${updatedBy.slice(0, 8)}...`}
        </span>
      )}
    </div>
    
    {warning && (
      <Alert className="border-amber-500/20 bg-amber-500/10 mb-4">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-700 text-xs">{warning}</AlertDescription>
      </Alert>
    )}
    
    <div className="space-y-4">
      {children}
    </div>
    
    {notes.length > 0 && (
      <div className="mt-4 pt-3 border-t border-[#e5e2dd]">
        {notes.map((note, i) => (
          <p key={i} className="text-xs text-[#888] italic">{note}</p>
        ))}
      </div>
    )}
    
    <Button
      onClick={onSave}
      disabled={saving || !dirty}
      size="sm"
      className={`mt-4 ${dirty ? 'bg-[#f0b90b] hover:bg-[#d4a50a]' : 'bg-gray-300'} text-white`}
    >
      {saving ? (
        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...</>
      ) : (
        <><Save className="w-3 h-3 mr-1" /> Save Section</>
      )}
    </Button>
  </Card>
);

// Tooltips copy - all values are fractions (0–1), NOT percentages
const TOOLTIPS = {
  commonFunder: {
    enabled: "When ON, penalizes extra tips to the same creator from wallets linked to the same funder. When OFF, no common-funder penalties apply.",
    funderLookbackDays: "How far back (in days) we look to determine a wallet's funder for linking.",
    minWallets: "Minimum number of linked wallets required before this enforcement triggers.",
    penaltyPercent: "Fraction of points removed (0–1). Example: 0.30 = 30% penalty, affected tips keep 70%.",
    p: "Sensitivity parameter (0–1). Higher values make enforcement stricter. 0 = disabled, 1 = maximum."
  },
  kickback: {
    kickbackEnabled: "When ON, penalizes tip relationships where the creator returns TIP tokens to the tipper (wash trading/kickback).",
    kickbackWindowDays: "Time window (in days) after a tip during which return transfers trigger detection.",
    kickbackMinAmountTIP: "Minimum TIP token amount of a return transfer to count as kickback evidence.",
    kickbackPenaltyPercent: "Fraction of points removed (0–1). Example: 0.30 = 30% penalty, affected tips keep 70%.",
    tipmeTokenAddress: "TIP token contract address used to detect kickback transfers."
  },
  spendRatio: {
    spendRatioEnabled: "When ON, penalizes wallets that tip above a threshold compared to their snapshot balance.",
    spendRatioThresholdX: "Spend ratio threshold. If wallet's spend_ratio exceeds this value, penalty applies.",
    spendRatioMultiplierY: "Fraction of points kept when triggered (0–1). Example: 0.70 = keep 70% of points."
  },
  shareCap: {
    epochShareCapEnabled: "When ON, caps how much of the pool a single wallet can capture. When OFF, no cap is enforced.",
    epochShareCapPct: "Maximum share per wallet as a fraction (0–1). Example: 0.01 = 1% cap, 0.05 = 5% cap."
  },
  verification: {
    creator_verified_weight: "Multiplier applied to verified creators' creator-points. Example: 1.0 = full weight.",
    creator_unverified_weight: "Multiplier applied to unverified creators' creator-points. Example: 0.0 = no points, 0.3 = 30% weight."
  }
};

export default function ScoringConfigV2({ adminWallet, getAuthHeaders }) {
  // Loading states
  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);
  const [savingSection, setSavingSection] = useState({});
  
  // Config states
  const [enforcement, setEnforcement] = useState({
    enabled: true,
    p: 0.25,
    minWallets: 5,
    penaltyPercent: 0.30,
    funderLookbackDays: 7
  });
  
  const [kickback, setKickback] = useState({
    kickbackEnabled: true,
    kickbackPenaltyPercent: 0.30,
    kickbackWindowDays: 30,
    kickbackMinAmountTIP: 1.0,
    tipmeTokenAddress: ''
  });
  
  const [spendRatio, setSpendRatio] = useState({
    spendRatioEnabled: true,
    spendRatioThresholdX: 0.5,
    spendRatioMultiplierY: 0.7
  });
  
  const [shareCap, setShareCap] = useState({
    epochShareCapEnabled: true,
    epochShareCapPct: 0.01
  });
  
  const [walletQuality, setWalletQuality] = useState({
    enabled: false,
    qBase: 0.75
  });
  
  const [poolRatios, setPoolRatios] = useState({
    tipperPoolRatio: 0.70
  });
  
  const [verification, setVerification] = useState({
    creator_verified_weight: 1.0,
    creator_unverified_weight: 0.0
  });
  
  // Original values for dirty checking
  const [original, setOriginal] = useState({});
  
  // Metadata
  const [metadata, setMetadata] = useState({
    enforcement: { updatedAt: null, updatedBy: null },
    kickback: { updatedAt: null, updatedBy: null },
    spendRatio: { updatedAt: null, updatedBy: null },
    shareCap: { updatedAt: null, updatedBy: null },
    walletQuality: { updatedAt: null, updatedBy: null },
    poolRatios: { updatedAt: null, updatedBy: null }
  });
  
  // Validation errors
  const [errors, setErrors] = useState({});
  
  // State map for safe section lookup (replaces unsafe eval)
  const getStateBySection = (section) => {
    const stateMap = {
      enforcement,
      kickback,
      spendRatio,
      shareCap,
      walletQuality,
      poolRatios,
      verification
    };
    return stateMap[section];
  };
  
  // Check if section is dirty
  const isDirty = (section) => {
    return JSON.stringify(getStateBySection(section)) !== JSON.stringify(original[section]);
  };
  
  // Fetch all configs — each section loads independently (one failure doesn't break others)
  const fetchConfigs = useCallback(async () => {
    if (!adminWallet || !getAuthHeaders) return;
    
    setLoading(true);
    const authHeaders = getAuthHeaders();
    
    const results = await Promise.allSettled([
      axios.get(`${BACKEND_URL}/api/admin/scoring-config/enforcement`, { headers: authHeaders }),
      axios.get(`${BACKEND_URL}/api/admin/scoring-config/kickback`, { headers: authHeaders }),
      axios.get(`${BACKEND_URL}/api/admin/scoring-config/spend-ratio`, { headers: authHeaders }),
      axios.get(`${BACKEND_URL}/api/admin/scoring-config/epoch-share-cap`, { headers: authHeaders }),
      axios.get(`${BACKEND_URL}/api/admin/scoring-config/q-multiplier`, { headers: authHeaders }),
      axios.get(`${BACKEND_URL}/api/admin/scoring-config/pool-ratios`, { headers: authHeaders }),
      axios.get(`${BACKEND_URL}/api/admin/scoring-settings`, { headers: authHeaders })
    ]);
    
    const [enfRes, kbRes, srRes, scRes, wqRes, prRes, settingsRes] = results;
    const failedSections = [];
    
    // Enforcement (Phase 1)
    if (enfRes.status === 'fulfilled') {
      const enf = enfRes.value.data.enforcement || {};
      const enfData = {
        enabled: enf.enabled ?? true,
        p: enf.p ?? 0.25,
        minWallets: enf.minWallets ?? 5,
        penaltyPercent: enf.penaltyPercent ?? 0.30,
        funderLookbackDays: enf.funderLookbackDays ?? 7
      };
      setEnforcement(enfData);
      setMetadata(m => ({ ...m, enforcement: { updatedAt: enf.updatedAt, updatedBy: enf.updatedBy }}));
      setOriginal(o => ({ ...o, enforcement: enfData }));
    } else {
      console.error('Failed to load enforcement config:', enfRes.reason?.response?.data || enfRes.reason?.message);
      failedSections.push('enforcement');
    }
    
    // Kickback (Phase 2)
    if (kbRes.status === 'fulfilled') {
      const kb = kbRes.value.data.kickback || {};
      const kbData = {
        kickbackEnabled: kb.kickbackEnabled ?? true,
        kickbackPenaltyPercent: kb.kickbackPenaltyPercent ?? 0.30,
        kickbackWindowDays: kb.kickbackWindowDays ?? 30,
        kickbackMinAmountTIP: kb.kickbackMinAmountTIP ?? 1.0,
        tipmeTokenAddress: kb.tipmeTokenAddress || ''
      };
      setKickback(kbData);
      setMetadata(m => ({ ...m, kickback: { updatedAt: kb.updatedAt, updatedBy: kb.updatedBy }}));
      setOriginal(o => ({ ...o, kickback: kbData }));
    } else {
      console.error('Failed to load kickback config:', kbRes.reason?.response?.data || kbRes.reason?.message);
      failedSections.push('kickback');
    }
    
    // Spend Ratio
    if (srRes.status === 'fulfilled') {
      const sr = srRes.value.data.spendRatio || {};
      const srData = {
        spendRatioEnabled: sr.spendRatioEnabled ?? true,
        spendRatioThresholdX: sr.spendRatioThresholdX ?? 0.5,
        spendRatioMultiplierY: sr.spendRatioMultiplierY ?? 0.7
      };
      setSpendRatio(srData);
      setMetadata(m => ({ ...m, spendRatio: { updatedAt: sr.updatedAt, updatedBy: sr.updatedBy }}));
      setOriginal(o => ({ ...o, spendRatio: srData }));
    } else {
      console.error('Failed to load spend ratio config:', srRes.reason?.response?.data || srRes.reason?.message);
      failedSections.push('spendRatio');
    }
    
    // Share Cap (Phase 3)
    if (scRes.status === 'fulfilled') {
      const sc = scRes.value.data.shareCap || {};
      const scData = {
        epochShareCapEnabled: sc.epochShareCapEnabled ?? true,
        epochShareCapPct: sc.epochShareCapPct ?? 0.01
      };
      setShareCap(scData);
      setMetadata(m => ({ ...m, shareCap: { updatedAt: sc.updatedAt, updatedBy: sc.updatedBy }}));
      setOriginal(o => ({ ...o, shareCap: scData }));
    } else {
      console.error('Failed to load share cap config:', scRes.reason?.response?.data || scRes.reason?.message);
      failedSections.push('shareCap');
    }
    
    // Wallet Quality (Q Multiplier)
    if (wqRes.status === 'fulfilled') {
      const wq = wqRes.value.data.qMultiplier || {};
      const wqData = {
        enabled: wq.enabled ?? false,
        qBase: wq.qBase ?? 0.75
      };
      setWalletQuality(wqData);
      setMetadata(m => ({ ...m, walletQuality: { updatedAt: wq.updatedAt, updatedBy: wq.updatedBy }}));
      setOriginal(o => ({ ...o, walletQuality: wqData }));
    } else {
      console.error('Failed to load Q multiplier config:', wqRes.reason?.response?.data || wqRes.reason?.message);
      failedSections.push('walletQuality');
    }
    
    // Pool Ratios (Tipper/Creator Split)
    if (prRes.status === 'fulfilled') {
      const pr = prRes.value.data.scoringPools || {};
      const prData = {
        tipperPoolRatio: pr.tipperPoolRatio ?? 0.70
      };
      setPoolRatios(prData);
      setMetadata(m => ({ ...m, poolRatios: { updatedAt: pr.updatedAt, updatedBy: pr.updatedBy }}));
      setOriginal(o => ({ ...o, poolRatios: prData }));
    } else {
      console.error('Failed to load pool ratios config:', prRes.reason?.response?.data || prRes.reason?.message);
      failedSections.push('poolRatios');
    }
    
    // Verification Weights
    if (settingsRes.status === 'fulfilled') {
      const settings = settingsRes.value.data || {};
      const vData = {
        creator_verified_weight: settings.creator_verified_weight ?? 1.0,
        creator_unverified_weight: settings.creator_unverified_weight ?? 0.0
      };
      setVerification(vData);
      setOriginal(o => ({ ...o, verification: vData }));
    } else {
      console.error('Failed to load verification weights:', settingsRes.reason?.response?.data || settingsRes.reason?.message);
      failedSections.push('verification');
    }
    
    if (failedSections.length > 0) {
      toast.error(`Failed to load: ${failedSections.join(', ')}. Other sections loaded OK.`);
    }
    
    setLoading(false);
  }, [adminWallet, getAuthHeaders]);
  
  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);
  
  // Validate all sections
  const validateAll = () => {
    const newErrors = {};
    
    // Enforcement validation
    // p: sensitivity parameter, 0-1 fraction (0 = no effect, 1 = max strictness)
    if (enforcement.p < 0 || enforcement.p > 1) {
      newErrors['enforcement.p'] = 'Must be 0–1 (fraction)';
    }
    if (enforcement.minWallets < 2) {
      newErrors['enforcement.minWallets'] = 'Must be at least 2';
    }
    // penaltyPercent: 0.30 = 30% penalty, keeps 70%
    if (enforcement.penaltyPercent < 0 || enforcement.penaltyPercent > 1) {
      newErrors['enforcement.penaltyPercent'] = 'Must be 0–1 (fraction, e.g., 0.30 = 30%)';
    }
    if (enforcement.funderLookbackDays < 1) {
      newErrors['enforcement.funderLookbackDays'] = 'Must be at least 1';
    }
    
    // Kickback validation
    // kickbackPenaltyPercent: 0.30 = 30% penalty, keeps 70%
    if (kickback.kickbackPenaltyPercent < 0 || kickback.kickbackPenaltyPercent > 1) {
      newErrors['kickback.kickbackPenaltyPercent'] = 'Must be 0–1 (fraction, e.g., 0.30 = 30%)';
    }
    if (kickback.kickbackWindowDays < 1) {
      newErrors['kickback.kickbackWindowDays'] = 'Must be at least 1';
    }
    if (kickback.kickbackMinAmountTIP < 0) {
      newErrors['kickback.kickbackMinAmountTIP'] = 'Must be >= 0';
    }
    if (kickback.tipmeTokenAddress && !/^0x[a-fA-F0-9]{40}$/.test(kickback.tipmeTokenAddress)) {
      newErrors['kickback.tipmeTokenAddress'] = 'Invalid address format';
    }
    
    // Spend ratio validation
    // spendRatioThresholdX: threshold above which penalty triggers
    if (spendRatio.spendRatioThresholdX < 0) {
      newErrors['spendRatio.spendRatioThresholdX'] = 'Must be >= 0';
    }
    // spendRatioMultiplierY: 0.70 = keeps 70% when triggered
    if (spendRatio.spendRatioMultiplierY < 0 || spendRatio.spendRatioMultiplierY > 1) {
      newErrors['spendRatio.spendRatioMultiplierY'] = 'Must be 0–1 (fraction, e.g., 0.70 = keeps 70%)';
    }
    
    // Share cap validation
    // epochShareCapPct: 0.01 = 1% max share per wallet
    if (shareCap.epochShareCapPct <= 0 || shareCap.epochShareCapPct > 1) {
      newErrors['shareCap.epochShareCapPct'] = 'Must be 0–1 (fraction, e.g., 0.01 = 1% cap)';
    }
    
    // Wallet Quality validation
    // qBase: base Q score, 0-1 (higher = less punitive)
    if (walletQuality.qBase < 0 || walletQuality.qBase > 1) {
      newErrors['walletQuality.qBase'] = 'Must be 0–1 (e.g., 0.75 default)';
    }
    
    // Pool Ratios validation
    // tipperPoolRatio: must be strictly between 0 and 1
    if (poolRatios.tipperPoolRatio <= 0 || poolRatios.tipperPoolRatio >= 1) {
      newErrors['poolRatios.tipperPoolRatio'] = 'Must be > 0 and < 1 (e.g., 0.70 = 70% tipper / 30% creator)';
    }
    
    // Verification validation
    if (verification.creator_verified_weight < 0) {
      newErrors['verification.creator_verified_weight'] = 'Must be >= 0';
    }
    if (verification.creator_unverified_weight < 0) {
      newErrors['verification.creator_unverified_weight'] = 'Must be >= 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Save individual section
  const saveSection = async (section) => {
    if (!validateAll()) {
      toast.error('Please fix validation errors first');
      return;
    }
    
    if (!getAuthHeaders) {
      toast.error('Not authenticated');
      return;
    }
    
    const authHeaders = getAuthHeaders();
    setSavingSection(s => ({ ...s, [section]: true }));
    
    try {
      let endpoint, data;
      
      switch (section) {
        case 'enforcement':
          endpoint = '/api/admin/scoring-config/enforcement';
          data = enforcement;
          break;
        case 'kickback':
          endpoint = '/api/admin/scoring-config/kickback';
          data = kickback;
          break;
        case 'spendRatio':
          endpoint = '/api/admin/scoring-config/spend-ratio';
          data = spendRatio;
          break;
        case 'shareCap':
          endpoint = '/api/admin/scoring-config/epoch-share-cap';
          data = shareCap;
          break;
        case 'walletQuality':
          endpoint = '/api/admin/scoring-config/q-multiplier';
          data = walletQuality;
          break;
        case 'poolRatios':
          endpoint = '/api/admin/scoring-config/pool-ratios';
          data = poolRatios;
          break;
        case 'verification':
          // Verification uses query params for weights but auth headers for auth
          const params = new URLSearchParams({
            creator_verified_weight: verification.creator_verified_weight,
            creator_unverified_weight: verification.creator_unverified_weight
          });
          await axios.post(`${BACKEND_URL}/api/admin/scoring-settings?${params.toString()}`, {}, { headers: authHeaders });
          setOriginal(o => ({ ...o, verification: { ...verification } }));
          toast.success('Verification weights saved');
          setSavingSection(s => ({ ...s, [section]: false }));
          return;
        default:
          throw new Error('Unknown section');
      }
      
      await axios.post(`${BACKEND_URL}${endpoint}`, data, { headers: authHeaders });
      
      // Re-fetch this section to confirm persistence
      try {
        const getEndpoint = endpoint;
        const refreshRes = await axios.get(`${BACKEND_URL}${getEndpoint}`, { headers: authHeaders });
        const refreshData = refreshRes.data;
        
        // Update state with what the server returned
        if (section === 'enforcement' && refreshData.enforcement) {
          const enf = refreshData.enforcement;
          const enfData = { enabled: enf.enabled ?? true, p: enf.p ?? 0.25, minWallets: enf.minWallets ?? 5, penaltyPercent: enf.penaltyPercent ?? 0.30, funderLookbackDays: enf.funderLookbackDays ?? 7 };
          setEnforcement(enfData);
          setOriginal(o => ({ ...o, enforcement: enfData }));
        } else if (section === 'kickback' && refreshData.kickback) {
          const kb = refreshData.kickback;
          const kbData = { kickbackEnabled: kb.kickbackEnabled ?? true, kickbackPenaltyPercent: kb.kickbackPenaltyPercent ?? 0.30, kickbackWindowDays: kb.kickbackWindowDays ?? 30, kickbackMinAmountTIP: kb.kickbackMinAmountTIP ?? 1.0, tipmeTokenAddress: kb.tipmeTokenAddress || '' };
          setKickback(kbData);
          setOriginal(o => ({ ...o, kickback: kbData }));
        } else if (section === 'spendRatio' && refreshData.spendRatio) {
          const sr = refreshData.spendRatio;
          const srData = { spendRatioEnabled: sr.spendRatioEnabled ?? true, spendRatioThresholdX: sr.spendRatioThresholdX ?? 0.5, spendRatioMultiplierY: sr.spendRatioMultiplierY ?? 0.7 };
          setSpendRatio(srData);
          setOriginal(o => ({ ...o, spendRatio: srData }));
        } else if (section === 'shareCap' && refreshData.shareCap) {
          const sc = refreshData.shareCap;
          const scData = { epochShareCapEnabled: sc.epochShareCapEnabled ?? true, epochShareCapPct: sc.epochShareCapPct ?? 0.01 };
          setShareCap(scData);
          setOriginal(o => ({ ...o, shareCap: scData }));
        } else if (section === 'walletQuality' && refreshData.qMultiplier) {
          const wq = refreshData.qMultiplier;
          const wqData = { enabled: wq.enabled ?? false, qBase: wq.qBase ?? 0.75 };
          setWalletQuality(wqData);
          setOriginal(o => ({ ...o, walletQuality: wqData }));
        } else if (section === 'poolRatios' && refreshData.scoringPools) {
          const pr = refreshData.scoringPools;
          const prData = { tipperPoolRatio: pr.tipperPoolRatio ?? 0.70 };
          setPoolRatios(prData);
          setOriginal(o => ({ ...o, poolRatios: prData }));
        } else {
          setOriginal(o => ({ ...o, [section]: getStateBySection(section) }));
        }
      } catch (refreshErr) {
        console.warn(`Saved ${section} but refresh failed:`, refreshErr);
        setOriginal(o => ({ ...o, [section]: getStateBySection(section) }));
      }
      
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved`);
      
    } catch (error) {
      console.error(`Error saving ${section}:`, error);
      toast.error(error.response?.data?.detail || `Failed to save ${section}`);
    } finally {
      setSavingSection(s => ({ ...s, [section]: false }));
    }
  };
  
  // Save all sections
  const saveAll = async () => {
    if (!validateAll()) {
      toast.error('Please fix validation errors first');
      return;
    }
    
    setSavingAll(true);
    
    try {
      const sections = ['enforcement', 'kickback', 'spendRatio', 'shareCap', 'walletQuality', 'poolRatios', 'verification'];
      const dirtySections = sections.filter(s => isDirty(s));
      
      if (dirtySections.length === 0) {
        toast.info('No changes to save');
        setSavingAll(false);
        return;
      }
      
      for (const section of dirtySections) {
        await saveSection(section);
      }
      
      toast.success(`All scoring settings saved (${dirtySections.length} sections)`);
      
    } catch (error) {
      console.error('Error saving all:', error);
      toast.error('Failed to save all settings');
    } finally {
      setSavingAll(false);
    }
  };
  
  if (loading) {
    return (
      <Card className="p-6 bg-white border-[#b8b3ab]">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#f0b90b]" />
          <span className="ml-2 text-[#666]">Loading scoring configuration...</span>
        </div>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header Status Card */}
      <Card className="p-4 bg-gradient-to-r from-[#1a1a1a] to-[#2a2a2a] border-none">
        <div className="flex items-center gap-2 mb-3">
          <Settings className="w-5 h-5 text-[#f0b90b]" />
          <h2 className="text-lg font-bold text-white">Scoring Engine V2</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge label="Engine" value="V2 Unified Canonical" variant="success" />
          <StatusBadge label="Sybil V1" value="Removed" variant="danger" />
          <StatusBadge label="QR_R" value="Hard-disabled" variant="warning" />
          <StatusBadge label="Q Multiplier" value="ENV-controlled" variant="info" />
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Config sources: db.scoring_config(_id: singleton) + db.settings(scoring_settings)
        </p>
      </Card>
      
      {/* Phase 1: Common Funder Enforcement */}
      <MechanismCard
        title="Phase 1 - Common Funder Enforcement"
        onSave={() => saveSection('enforcement')}
        saving={savingSection.enforcement}
        dirty={isDirty('enforcement')}
        lastUpdated={metadata.enforcement?.updatedAt}
        updatedBy={metadata.enforcement?.updatedBy}
      >
        <ConfigToggle
          label="Enabled"
          tooltip={TOOLTIPS.commonFunder.enabled}
          checked={enforcement.enabled}
          onChange={(v) => setEnforcement({ ...enforcement, enabled: v })}
        />
        
        <div className={`space-y-4 ${!enforcement.enabled ? 'opacity-50' : ''}`}>
          <ConfigInput
            label="Lookback (days)"
            tooltip={TOOLTIPS.commonFunder.funderLookbackDays}
            value={enforcement.funderLookbackDays}
            onChange={(e) => setEnforcement({ ...enforcement, funderLookbackDays: parseInt(e.target.value) || 7 })}
            type="number"
            step="1"
            min={1}
            disabled={!enforcement.enabled}
            error={errors['enforcement.funderLookbackDays']}
          />
          
          <ConfigInput
            label="Minimum linked wallets"
            tooltip={TOOLTIPS.commonFunder.minWallets}
            value={enforcement.minWallets}
            onChange={(e) => setEnforcement({ ...enforcement, minWallets: parseInt(e.target.value) || 5 })}
            type="number"
            step="1"
            min={2}
            disabled={!enforcement.enabled}
            error={errors['enforcement.minWallets']}
          />
          
          <ConfigInput
            label="Penalty percent"
            tooltip={TOOLTIPS.commonFunder.penaltyPercent}
            value={enforcement.penaltyPercent}
            onChange={(e) => setEnforcement({ ...enforcement, penaltyPercent: parseFloat(e.target.value) || 0.30 })}
            min={0}
            max={1}
            disabled={!enforcement.enabled}
            error={errors['enforcement.penaltyPercent']}
            suffix={`(keeps ${((1 - enforcement.penaltyPercent) * 100).toFixed(0)}%)`}
          />
          
          <ConfigInput
            label="Sensitivity (p)"
            tooltip={TOOLTIPS.commonFunder.p}
            value={enforcement.p}
            onChange={(e) => setEnforcement({ ...enforcement, p: parseFloat(e.target.value) || 0.25 })}
            min={0}
            max={1}
            disabled={!enforcement.enabled}
            error={errors['enforcement.p']}
          />
        </div>
      </MechanismCard>
      
      {/* Phase 2: Kickback / Wash Trading */}
      <MechanismCard
        title="Phase 2 - Kickback / Wash Trading"
        onSave={() => saveSection('kickback')}
        saving={savingSection.kickback}
        dirty={isDirty('kickback')}
        lastUpdated={metadata.kickback?.updatedAt}
        updatedBy={metadata.kickback?.updatedBy}
      >
        <ConfigToggle
          label="Enabled"
          tooltip={TOOLTIPS.kickback.kickbackEnabled}
          checked={kickback.kickbackEnabled}
          onChange={(v) => setKickback({ ...kickback, kickbackEnabled: v })}
        />
        
        <div className={`space-y-4 ${!kickback.kickbackEnabled ? 'opacity-50' : ''}`}>
          <ConfigInput
            label="Window (days)"
            tooltip={TOOLTIPS.kickback.kickbackWindowDays}
            value={kickback.kickbackWindowDays}
            onChange={(e) => setKickback({ ...kickback, kickbackWindowDays: parseInt(e.target.value) || 30 })}
            type="number"
            step="1"
            min={1}
            disabled={!kickback.kickbackEnabled}
            error={errors['kickback.kickbackWindowDays']}
          />
          
          <ConfigInput
            label="Minimum return amount (TIP)"
            tooltip={TOOLTIPS.kickback.kickbackMinAmountTIP}
            value={kickback.kickbackMinAmountTIP}
            onChange={(e) => setKickback({ ...kickback, kickbackMinAmountTIP: parseFloat(e.target.value) || 1.0 })}
            min={0}
            disabled={!kickback.kickbackEnabled}
            error={errors['kickback.kickbackMinAmountTIP']}
          />
          
          <ConfigInput
            label="Penalty percent"
            tooltip={TOOLTIPS.kickback.kickbackPenaltyPercent}
            value={kickback.kickbackPenaltyPercent}
            onChange={(e) => setKickback({ ...kickback, kickbackPenaltyPercent: parseFloat(e.target.value) || 0.30 })}
            min={0}
            max={1}
            disabled={!kickback.kickbackEnabled}
            error={errors['kickback.kickbackPenaltyPercent']}
            suffix={`(keeps ${((1 - kickback.kickbackPenaltyPercent) * 100).toFixed(0)}%)`}
          />
          
          <ConfigInput
            label="TIP token address"
            tooltip={TOOLTIPS.kickback.tipmeTokenAddress}
            value={kickback.tipmeTokenAddress}
            onChange={(e) => setKickback({ ...kickback, tipmeTokenAddress: e.target.value.toLowerCase() })}
            type="text"
            disabled={!kickback.kickbackEnabled}
            error={errors['kickback.tipmeTokenAddress']}
            placeholder="0x..."
          />
        </div>
      </MechanismCard>
      
      {/* Spend Ratio */}
      <MechanismCard
        title="Spend Ratio"
        onSave={() => saveSection('spendRatio')}
        saving={savingSection.spendRatio}
        dirty={isDirty('spendRatio')}
        lastUpdated={metadata.spendRatio?.updatedAt}
        updatedBy={metadata.spendRatio?.updatedBy}
        notes={["Snapshot mode is fixed in code (not configurable here)."]}
      >
        <ConfigToggle
          label="Enabled"
          tooltip={TOOLTIPS.spendRatio.spendRatioEnabled}
          checked={spendRatio.spendRatioEnabled}
          onChange={(v) => setSpendRatio({ ...spendRatio, spendRatioEnabled: v })}
        />
        
        <div className={`space-y-4 ${!spendRatio.spendRatioEnabled ? 'opacity-50' : ''}`}>
          <ConfigInput
            label="Threshold X"
            tooltip={TOOLTIPS.spendRatio.spendRatioThresholdX}
            value={spendRatio.spendRatioThresholdX}
            onChange={(e) => setSpendRatio({ ...spendRatio, spendRatioThresholdX: parseFloat(e.target.value) || 0.5 })}
            min={0}
            disabled={!spendRatio.spendRatioEnabled}
            error={errors['spendRatio.spendRatioThresholdX']}
          />
          
          <ConfigInput
            label="Multiplier Y"
            tooltip={TOOLTIPS.spendRatio.spendRatioMultiplierY}
            value={spendRatio.spendRatioMultiplierY}
            onChange={(e) => setSpendRatio({ ...spendRatio, spendRatioMultiplierY: parseFloat(e.target.value) || 0.7 })}
            min={0}
            max={1}
            disabled={!spendRatio.spendRatioEnabled}
            error={errors['spendRatio.spendRatioMultiplierY']}
            suffix={`(keeps ${(spendRatio.spendRatioMultiplierY * 100).toFixed(0)}%)`}
          />
        </div>
      </MechanismCard>
      
      {/* Phase 3: Share Cap (LAST) */}
      <MechanismCard
        title="Phase 3 - Share Cap"
        badge="LAST"
        warning="This is enforced AFTER all other multipliers/penalties. Do not reorder."
        onSave={() => saveSection('shareCap')}
        saving={savingSection.shareCap}
        dirty={isDirty('shareCap')}
        lastUpdated={metadata.shareCap?.updatedAt}
        updatedBy={metadata.shareCap?.updatedBy}
      >
        <ConfigToggle
          label="Enabled"
          tooltip={TOOLTIPS.shareCap.epochShareCapEnabled}
          checked={shareCap.epochShareCapEnabled}
          onChange={(v) => setShareCap({ ...shareCap, epochShareCapEnabled: v })}
        />
        
        <div className={`space-y-4 ${!shareCap.epochShareCapEnabled ? 'opacity-50' : ''}`}>
          <ConfigInput
            label="Max share per wallet"
            tooltip={TOOLTIPS.shareCap.epochShareCapPct}
            value={shareCap.epochShareCapPct}
            onChange={(e) => setShareCap({ ...shareCap, epochShareCapPct: parseFloat(e.target.value) || 0.01 })}
            min={0}
            max={1}
            disabled={!shareCap.epochShareCapEnabled}
            error={errors['shareCap.epochShareCapPct']}
            suffix={`(${(shareCap.epochShareCapPct * 100).toFixed(1)}% cap)`}
          />
        </div>
      </MechanismCard>
      
      {/* Verification Weights */}
      <MechanismCard
        title="Verification Weights"
        onSave={() => saveSection('verification')}
        saving={savingSection.verification}
        dirty={isDirty('verification')}
        notes={["No enable flag exists in backend; these weights always apply."]}
      >
        <ConfigInput
          label="Verified creator weight"
          tooltip={TOOLTIPS.verification.creator_verified_weight}
          value={verification.creator_verified_weight}
          onChange={(e) => setVerification({ ...verification, creator_verified_weight: parseFloat(e.target.value) || 1.0 })}
          min={0}
          error={errors['verification.creator_verified_weight']}
        />
        
        <ConfigInput
          label="Unverified creator weight"
          tooltip={TOOLTIPS.verification.creator_unverified_weight}
          value={verification.creator_unverified_weight}
          onChange={(e) => setVerification({ ...verification, creator_unverified_weight: parseFloat(e.target.value) || 0.0 })}
          min={0}
          error={errors['verification.creator_unverified_weight']}
        />
      </MechanismCard>
      
      {/* Q Multiplier (Wallet Quality) - Now Configurable */}
      <MechanismCard
        title="Wallet Quality (Q Multiplier)"
        badge="Stage 7"
        onSave={() => saveSection('walletQuality')}
        saving={savingSection.walletQuality}
        dirty={isDirty('walletQuality')}
        lastUpdated={metadata.walletQuality?.updatedAt}
        updatedBy={metadata.walletQuality?.updatedBy}
        notes={[
          "Q is applied to TIPPER scores only (not creators).",
          "Q_epoch: +0.10 per active epoch (max 5), -0.10 per inactive epoch.",
          "Q_concentration: -0.20 (1 day), -0.10 (2 days) based on current epoch.",
          "Q_diversity: -0.15 (≤2 counterparties), -0.05 (3-4) based on current epoch."
        ]}
      >
        <ConfigToggle
          label="Q Multiplier Enabled"
          tooltip="When ON, applies Q multiplier to tipper scores. When OFF, all wallets effectively have Q=1.0 (no adjustment)."
          checked={walletQuality.enabled}
          onChange={(v) => setWalletQuality({ ...walletQuality, enabled: v })}
        />
        
        <div className={`space-y-4 ${!walletQuality.enabled ? 'opacity-50' : ''}`}>
          <ConfigInput
            label="Q Base"
            tooltip="Base Q score for all wallets (0–1). Higher = less punitive. Default: 0.75"
            value={walletQuality.qBase}
            onChange={(e) => setWalletQuality({ ...walletQuality, qBase: parseFloat(e.target.value) || 0.75 })}
            min={0}
            max={1}
            step={0.01}
            disabled={!walletQuality.enabled}
            error={errors['walletQuality.qBase']}
            suffix={`(starting point: ${(walletQuality.qBase * 100).toFixed(0)}%)`}
          />
        </div>
        
        <div className="mt-3 pt-3 border-t border-[#e5e2dd] text-xs text-[#888] space-y-1">
          <p><strong>Status:</strong> {walletQuality.enabled ? '✅ Q applied to tipper scores' : '❌ Q disabled (all wallets = 1.0)'}</p>
          <p><strong>Q Range:</strong> 0.50 (min) → 1.00 (max), Newcomer cap: 0.85</p>
        </div>
      </MechanismCard>
      
      {/* Pool Ratios (Tipper/Creator Split) */}
      <MechanismCard
        title="Pool Ratios (Tipper/Creator Split)"
        badge="Core"
        onSave={() => saveSection('poolRatios')}
        saving={savingSection.poolRatios}
        dirty={isDirty('poolRatios')}
        lastUpdated={metadata.poolRatios?.updatedAt}
        updatedBy={metadata.poolRatios?.updatedBy}
        warning={
          original.poolRatios?.tipperPoolRatio && 
          Math.abs(poolRatios.tipperPoolRatio - original.poolRatios.tipperPoolRatio) > 0.05
            ? `⚠️ Significant change: ${(original.poolRatios.tipperPoolRatio * 100).toFixed(1)}% → ${(poolRatios.tipperPoolRatio * 100).toFixed(1)}% tipper ratio`
            : null
        }
        notes={[
          "Creator ratio is computed as: 1 - tipperPoolRatio (never stored separately).",
          "Changes affect future epoch computations only, not past epochs."
        ]}
      >
        <ConfigInput
          label="Tipper Pool Ratio"
          tooltip="Fraction of total pool allocated to tippers (0–1). Creator pool = 1 - tipper pool. Example: 0.70 = 70% tipper / 30% creator."
          value={poolRatios.tipperPoolRatio}
          onChange={(e) => {
            const val = parseFloat(e.target.value);
            if (!isNaN(val)) {
              setPoolRatios({ ...poolRatios, tipperPoolRatio: val });
            }
          }}
          min={0.0001}
          max={0.9999}
          step={0.0001}
          error={errors['poolRatios.tipperPoolRatio']}
        />
        
        {/* Computed Creator Ratio (Read-Only) */}
        <div className="space-y-1 mt-4">
          <label className="text-sm text-[#666] flex items-center">
            Creator Pool Ratio
            <span className="ml-2 text-xs text-[#888] bg-[#e5e2dd] px-2 py-0.5 rounded">Computed</span>
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={(1 - poolRatios.tipperPoolRatio).toFixed(4)}
              disabled={true}
              className="bg-[#e5e2dd] border-[#b8b3ab] opacity-70 cursor-not-allowed"
            />
            <span className="text-sm text-[#888]">(read-only)</span>
          </div>
        </div>
        
        {/* Visual Split Display */}
        <div className="mt-4 pt-3 border-t border-[#e5e2dd]">
          <p className="text-xs text-[#666] mb-2">Distribution Preview:</p>
          <div className="flex h-6 rounded-lg overflow-hidden">
            <div 
              className="bg-blue-500 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${poolRatios.tipperPoolRatio * 100}%` }}
            >
              {(poolRatios.tipperPoolRatio * 100).toFixed(1)}% Tipper
            </div>
            <div 
              className="bg-green-500 flex items-center justify-center text-white text-xs font-medium"
              style={{ width: `${(1 - poolRatios.tipperPoolRatio) * 100}%` }}
            >
              {((1 - poolRatios.tipperPoolRatio) * 100).toFixed(1)}% Creator
            </div>
          </div>
        </div>
      </MechanismCard>
      
      {/* Save All Button */}
      <div className="flex justify-end pt-2">
        <Button
          onClick={saveAll}
          disabled={savingAll}
          size="lg"
          className="bg-[#f0b90b] hover:bg-[#d4a50a] text-white px-8"
        >
          {savingAll ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving All...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Save All Scoring Settings</>
          )}
        </Button>
      </div>
    </div>
  );
}
